<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-compare-metrics-chart-line">
    <div ref="chart" :id="`compare-metrics-${chartData.id}`" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import WyChartModule from '../../library/wy-chart-module.js';
import moment from 'moment';

export default Vue.extend({
  name: 'wy-analytics-compare-metrics-chart-line',  
  props: ['chartData', 'parent'],
  
  data() {
    return {
      wyChart: null,
      traceList: []
    }
  },

  computed: {
    ...mapGetters(['companyConfig']),
  },

  methods: {
    initData(type) {
      if(this.chartData && this.chartData.data && this.chartData.data.trend) {
        let map = {
          'sumPositive': {label: 'Positive', color: 'rgba(86, 187, 129, 0.8)'},
          'sumNeutral': {label: 'Neutral', color: 'rgba(165, 156, 156, 0.3)'},
          'sumNegative': {label: 'Negative', color: 'rgba(255, 154, 128, 0.7)'}
        }

        let dataItems;        
        if(this.chartData.columns[0] == 'sumHandlingTime') {
          dataItems = this.chartData.data.trend.filter(el => el.sumHandlingNotNegative >= 0);
        }
        else if(this.chartData.columns[0] == 'sumResponseTime') {
          dataItems = this.chartData.data.trend.filter(el => el.sumResponseNotNegative >= 0);
        }
        else {
          dataItems = this.chartData.data.trend;
        }
      
        if(dataItems.length > 0) {
          // prepare date range   
          let dateStart = moment(dataItems[0].date);
          let dateEnd = moment(dataItems[dataItems.length-1].date);
        
          this.wyChart.init(type, dateStart, dateEnd, null, (this.companyConfig.default || {}).selectedViewBy || "week");

          // prepare date types
          dataItems.forEach(el => {
            this.wyChart.prepareGroupDates(el, el.date);
          });

          // group by type
          let localList = [];          
          this.chartData.columns.forEach(column => {
            let dataList = this.wyChart.groupBy(dataItems, (items) => {
              let date = items[0].day;
              let sumConversations = 0;
              if(column == 'sumHandlingTime') {
                // eslint-disable-next-line no-undef
                sumConversations = _.sumBy(items, 'sumHandlingNotNegative');
              }
              else if(column == 'sumResponseTime') {
                // eslint-disable-next-line no-undef
                sumConversations = _.sumBy(items, 'sumResponseNotNegative');
              }
              else {
                // eslint-disable-next-line no-undef
                sumConversations = _.sumBy(items, 'sumConversations');
              }
              if(sumConversations == 0) sumConversations = 1;

              let value = 0;
              let partialDateRangeFlag = false;
              
              if(column == 'sumPositive' || column == 'sumNeutral' || column == 'sumNegative') {
                // eslint-disable-next-line no-undef
                let sumValue = _.sumBy(items, column)
                // eslint-disable-next-line no-undef
                let sumPositive = _.sumBy(items, 'sumPositive');
                // eslint-disable-next-line no-undef
                let sumNeutral = _.sumBy(items, 'sumNeutral');
                // eslint-disable-next-line no-undef
                let sumNegative = _.sumBy(items, 'sumNegative');
                let totalSentiment = sumPositive + sumNeutral + sumNegative;              
                value = (sumValue/totalSentiment*100).toFixed(2);
              }
              else if(column == 'sumResolved') {
                // eslint-disable-next-line no-undef
                value = (_.sumBy(items, column) / sumConversations) * 100
              }
              else if(this.chartData.suffix == 'm') {
                // eslint-disable-next-line no-undef
                value = _.sumBy(items, column) / sumConversations
              }
              else if(this.chartData.suffix == '%' 
                || column == 'sumBesPercent' || column == 'sumBasPercent'
                || column == 'sumAvgAes'
              ) {
                // eslint-disable-next-line no-undef
                value = _.sumBy(items, column) / items.length
              }
              else {
                // eslint-disable-next-line no-undef
                value = _.sumBy(items, column)
                let dateRangeType = this.wyChart.getDateRangeType();
                if(column == 'sumVolumeDMValue'){
                  if((dateRangeType.type == 'week' && items.length <=4) || (dateRangeType.type == 'month' && items.length <=20)){
                    partialDateRangeFlag = true;
                  }
                }
              }
              if(value == -1) {
                value = null
              }
              return {
                date: date,
                value: value,
                partialDateRangeFlag: partialDateRangeFlag
              }
            });

            localList.push({
              column: column,
              label: (map[column] ? map[column].label : null),
              color: (map[column] ? map[column].color : null),
              dataList: dataList,
              dates: [],
              values: [],
              texts: []
            });
          });
          this.traceList = localList;

          // process median values
          if(localList.length == 1) {
            let trace = localList[0];
            let median = this.wyChart.prepareRegressionData(trace.dataList, {format: this.chartData.format});
            localList[0].medianData = median;
            
            return {
              dateRange: this.wyChart.selectedDateRange,
              minValue: median.data.minValue,
              maxValue: median.data.maxValue,
            }
          }
          else if(this.traceList.length > 1) {
            let minValue = localList[0].dataList.reduce((min, item) => { return (item.value < min) ? item.value : min; }, 0);
            let maxValue = localList[0].dataList.reduce((max, item) => { return (item.value > max) ? item.value : max; }, 0);
            return {
              dateRange: this.wyChart.selectedDateRange,
              minValue: minValue,
              maxValue: maxValue
            }
          }
        }
        else {
          let localList = [];
          this.chartData.columns.forEach(column => {
            localList.push({
              column: column,
              label: (map[column] ? map[column].label : null),
              color: (map[column] ? map[column].color : null),
              dataList: [],
              dates: [],
              values: [],
              texts: []
            });
          });
          
          if(localList.length == 1) {
            let trace = localList[0];
            let median = this.wyChart.prepareRegressionData(trace.dataList, {format: this.chartData.format});
            localList[0].medianData = median;
          }
          this.traceList = localList;

          return {
              dateRange: this.wyChart.selectedDateRange,
              minValue: 0,
              maxValue: 1
            }
        }
      }
    },

    buildChart(options) {
      if(this.traceList && this.traceList.length == 1) {
        let trace = this.traceList[0];
        let median = trace.medianData;

        let dateRangeType = this.wyChart.getDateRangeType();
        if(dateRangeType.type == 'week' || dateRangeType.type == 'day') {
          dateRangeType.tickformat = dateRangeType.tickformat.replace('Y', 'y');
        }

        let traceData = {
          name: this.chartData.title,
          type: "scatter",
          mode: "lines+markers",
          cliponaxis: false,
          //marker: {size: median.data.markerSize},
          marker: {color: median.data.markerColor, size: 8},
          line: {
            //shape: 'spline',
            color: '#214586'
          },
          x: median.data.x,
          y: median.data.y,
          text: median.data.medianTexts,
          hovertext: median.data.texts,
          hovertemplate: this.wyChart.hoverTemplateMedian(this.chartData.suffix),
        };

        if(median.data.y.length == 1) {
          // print marker if only one data item
          traceData.marker = {size: 8};
        }

        let traceMedianLine = {
          name: 'Median',
          type: 'scatter',
          mode: "lines",
          hoverinfo: 'none',
          x: median.medianData.x,
          y: median.medianData.y,
          line: {
            color: '#444',
            width: 1
          },
        };

        let traceRegression = {
          name: 'Trend',
          type: 'scatter',
          mode: "lines",
          x: median.regressionData.xValues,
          y: median.regressionData.yValues,
          hoverinfo: 'none',
          fill: 'none',
          line: {
            dash: 'dot',
            color: '#000',
            width: 1
          },          
        };

        let traces = [traceData, traceMedianLine, traceRegression];

        let resizableMargin = true;
        let monthView = dateRangeType.type == 'month'
        let yearView = dateRangeType.type == 'year'

        if(trace.dataList.length >= 6) {
          resizableMargin = false;
        }

        let layout = {
          autosize: true,
          //width: 408,
          height: 290,          
          dragmode: false, //'pan'
          separators: '.',
          showlegend: true,
          plot_bgcolor: 'white',
          hoverlabel: {
            namelength: 50,
            font: {
              size: 11
            },
            bordercolor: '#FCFEFF'
          },
          legend: {
            orientation: 'h',
            x: 0.15,
            y: -0.2
          },
          margin: { l: 45, r: 0, t: 10, b: 25 },
          xaxis:  {
            type: 'date',
            tickformat: dateRangeType.tickformat,
            tickvals: (monthView || yearView) && resizableMargin ? median.data.x : null,
            tickangle: 0,

            //tickmode: 'auto',
            //nticks: 5,
            autorange: true,
            fixedrange: true,
            //range: [median.data.x[0], median.data.x[median.data.x.length-1]],
            showgrid: false,
            automargin: true,
            zeroline: false,
            showline: true,
            linecolor: '#dedede',
            position: 0,
            layer: 'below traces',
            tickcolor: "transparent",
            tickfont: {
              color: '#8e9092',
              size: 9
            }
          },
          yaxis: {
            type: 'linear',
            autorange: true,
            rangemode: 'nonnegative',
            separatethousands: true,
            showgrid: true,
            showline: true,
            zeroline: false,
            linecolor: '#dedede',
            ticksuffix: (this.chartData.suffix + ' '),
            tickformat: (WyUtils[this.chartData.format] ? '' : (this.chartData.format == 'time' ? '' : Number(this.chartData.format))),
            fixedrange: true,
            layer: 'below traces',
            tickfont: {
              color: '#8e9092',
              size: 9
            }
          }
        }        
        this.wyChart.finalizeRegressionChart(traces, layout, this.chartData.format, options);
        
        let config = {responsive: true, displayModeBar: false, scrollZoom: false, showTips: false};
        // eslint-disable-next-line no-undef
        Plotly.newPlot(this.$refs.chart, traces, layout, config);

        this.$refs.chart.on('plotly_hover', (data) => {          
          try {
            let dataPoint = data.points[0];
            if(dataPoint && dataPoint.data && dataPoint.data.marker) {              
              let pn = dataPoint.pointNumber;
              // let sizes = dataPoint.data.marker.size;
              // sizes[pn] = 8;
              // let update = {'marker': {size: sizes}};
              let colors = dataPoint.data.marker.color;
              colors[pn] = "#214586";
              let update = {'marker': {color: colors}};
              // eslint-disable-next-line no-undef
              Plotly.relayout(this.$refs.chart, update, 0);
            }
          }
          catch {
            // catch error
          }
        });

        // mouse out on point
        this.$refs.chart.on('plotly_unhover', (data) => {
          try {
            let dataPoint = data.points[0];
            if(dataPoint && dataPoint.data && dataPoint.data.marker) {
              let pn = dataPoint.pointNumber;
              // let sizes = dataPoint.data.marker.size;
              // sizes[pn] = 0;
              // let update = {'marker': {size: sizes}};
              let colors = dataPoint.data.marker.color;            
              colors[pn] = "transparent";
              let update = {'marker': {color: colors}};
              // eslint-disable-next-line no-undef
              Plotly.relayout(this.$refs.chart, update, 0);
            }
          }
          catch {
            // catch error
          }
        });
      }
      else if(this.traceList && this.traceList.length > 0) {
        // ******************************** mutiple tracec chart ********************************
        let dateRangeType = this.wyChart.getDateRangeType();        
        if(dateRangeType.type == 'week' || dateRangeType.type == 'day') {
          dateRangeType.tickformat = dateRangeType.tickformat.replace('Y', 'y');
        }        

        this.traceList.forEach(trace => {          
          trace.dataList.forEach(item => {
            trace.dates.push(item.date);
            if(this.chartData.suffix == 'm') {
              trace.values.push(item.value);
              trace.texts.push(WyUtils.toNiceTimeFormatFromMinus(item.value));
            }
            else {
              trace.values.push(item.value);
              trace.texts.push(WyUtils.formatterNumber(item.value));
            }
          })
        });

        setTimeout(() => {
          let traces = [];
          this.traceList.forEach(trace => {
            let hovertemplate = this.wyChart.hoverTemplateNoDate(this.chartData.suffix);
            if(this.traceList.length > 1) {
              // sentiments
              hovertemplate = '<b>%{y}</b>';
            }

            traces.push({
              type: "scatter",
              mode: (trace.dates.length > 1 ? "none" : "markers"),
              fill: 'tonexty',
              fillcolor: trace.color,
              name: trace.label,
              line: {
                shape: 'spline',
                //color: trace.color,
                width: 0
              },
              stackgroup: 'one',
              x: trace.dates,
              y: trace.values,
              hovertext: trace.texts,
              hovertemplate: hovertemplate
            });
          });

          let layout = {
            autosize: true,
            height: 300,
            dragmode: false,
            //separators: ',.',
            showlegend: false,
            paper_bgcolor: '#FCFEFF',
            plot_bgcolor: '#FCFEFF',
            hoverlabel: {
              namelength: 50,
              font: {
                size: 11
              },
              bordercolor: '#FCFEFF'
            },
            margin: { l: 45, r: 20, t: 10, b: 25 },
            xaxis:  {
              type: 'date',
              tickformat: dateRangeType.tickformat,
              autorange: true,
              fixedrange: true,
              showgrid: true,
              zeroline: false,
              showline: true,
              linecolor: '#ccc',
              position: 0,
              tickcolor: "transparent",
              linewidth: 1,
              ticks: 'outside',
              ticklabelmode: 'period',
              tickfont: {
                color: '#8e9092',
                size: 9
              }
            },
            yaxis: {
              type: 'linear',
              autorange: true,
              rangemode: 'nonnegative',
              separatethousands: true,
              showgrid: false,
              showline: true,
              zeroline: false,
              linecolor: '#ccc',
              ticksuffix: (this.chartData.suffix + ' '),
              tickformat: (WyUtils[this.chartData.format] ? '' : Number(this.chartData.format)),
              fixedrange: true,
              linewidth: 2,
              tickfont: {
                color: '#8e9092',
                size: 9
              },
            }
          }
          this.wyChart.finalizeChart(traces, layout, options.maxYValue);

          let config = {responsive: true, displayModeBar: false, scrollZoom: false};
          // eslint-disable-next-line no-undef
          Plotly.newPlot(`compare-metrics-${this.chartData.id}`, traces, layout, config);
        }, 300)
      }
    }
  },

  created() {
    this.wyChart = new WyChartModule();
  }
});
</script>

<style lang="scss">
.wy-analytics-compare-metrics-chart-line {
  .js-plotly-plot .plotly .cursor-ew-resize {
    cursor: crosshair !important;
  }
  .plotly div {
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif !important;
  }
}
</style>